import { useMemo } from 'react';
import { useQuery, type UseQueryOptions, type UseQueryResult, type QueryKey } from '@tanstack/react-query';
import service from '../service';
import type { JobRun, JobRunType } from '@Types/jobRun';
import { stableStringify } from '@Utils/searchParams';

type JobRunsResponse =
  | JobRun[]
  | { data: JobRun[]; meta: Record<string, unknown> };

type UseJobRunsParams = {
  searchParams?: Record<string, unknown>;
  enabled?: boolean;
};

export function useJobRuns<TData = JobRunsResponse>(
  { searchParams, enabled = true }: UseJobRunsParams = {},
  options?: Omit<UseQueryOptions<JobRunsResponse, unknown, TData, QueryKey>, 'queryKey' | 'queryFn' | 'enabled'>,
): UseQueryResult<TData, unknown> {
  const stableParams = useMemo(() => stableStringify(searchParams), [searchParams]);

  const queryKey: QueryKey = ['job-runs', stableParams];

  return useQuery<JobRunsResponse, unknown, TData>({
    queryKey,
    enabled,
    queryFn: async () => service.getJobRuns(searchParams ?? {}),
    ...(options as any),
  });
}

export function useJobRun({ id, enabled = true }: { id?: string; enabled?: boolean }) {
  return useQuery<JobRun>({
    queryKey: ['job-run', id] as unknown as QueryKey,
    queryFn: () => service.getJobRun(id!),
    enabled: enabled && !!id,
  });
}

export function useJobRunTypes() {
  return useQuery<{ data: JobRunType[] }>({
    queryKey: ['job-run-types'] as unknown as QueryKey,
    queryFn: () => service.getJobRunTypes(),
  });
}

export function useJobRunsByType(typeName: string | undefined, options?: { from?: string; to?: string }) {
  return useQuery<{ data: JobRun[] }>({
    queryKey: ['job-runs-by-type', typeName, options?.from, options?.to] as unknown as QueryKey,
    queryFn: () => service.getJobRunsByType(typeName!, options),
    enabled: !!typeName,
  });
}
